import { cn } from '@/lib/utils'
import { Link, Loader2, Unlink } from 'lucide-react'
import { toast } from 'sonner'
import {
  useConnectWithings,
  useDisconnectWithings,
  useWithingsConnection
} from '../api/hooks/useWithingsAuth'
import { SettingsRow } from './ui/settings-row'
import { UnifiedButton } from './ui/unified-button'

interface WithingsConnectionProps {
  title?: string
  connectedDescription?: string
  disconnectedDescription?: string
  showAsRow?: boolean
  onConnectClick?: () => void
  onDisconnectClick?: () => void
  className?: string
  buttonVariant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom'
  buttonClassName?: string
}

export const WithingsConnection = ({
  title = 'Withings Account',
  connectedDescription = 'Connected to Withings for weight tracking',
  disconnectedDescription = 'Connect to track weight data',
  showAsRow = true,
  onConnectClick,
  onDisconnectClick,
  className,
  buttonVariant = 'outline',
  buttonSize = 'md'
}: WithingsConnectionProps) => {
  const { data: isWithingsConnected, isLoading: isWithingsLoading } = useWithingsConnection()
  const connectWithings = useConnectWithings()
  const disconnectWithings = useDisconnectWithings()

  const handleDisconnectWithings = () => {
    disconnectWithings.mutate(undefined, {
      onSuccess: () => {
        onDisconnectClick?.()
      },
      onError: (error) => {
        console.error('Failed to disconnect from Withings:', error)
        toast.error('Failed to disconnect from Withings')
      }
    })
  }

  const handleConnectWithings = () => {
    onConnectClick?.()
    connectWithings.mutate(undefined, {
      onError: (error) => {
        console.error('Failed to connect to Withings:', error)
        toast.error('Failed to connect to Withings')
      }
    })
  }

  const actionButton = isWithingsLoading ? (
    <UnifiedButton variant="ghost" size={buttonSize} disabled className={className}>
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      Loading...
    </UnifiedButton>
  ) : isWithingsConnected ? (
    <UnifiedButton
      variant="destructive"
      size={buttonSize}
      onClick={handleDisconnectWithings}
      disabled={disconnectWithings.isPending}
      className={cn(className, 'h-10')}
    >
      {disconnectWithings.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Unlink className="mr-2 h-4 w-4" />
      )}
      Disconnect
    </UnifiedButton>
  ) : (
    <UnifiedButton
      variant={buttonVariant}
      size={buttonSize}
      onClick={handleConnectWithings}
      disabled={connectWithings.isPending}
      className={className}
    >
      {connectWithings.isPending ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Connect
        </>
      ) : (
        <>
          <Link className="mr-2 h-4 w-4" />
          Connect with Withings
        </>
      )}
    </UnifiedButton>
  )

  if (showAsRow) {
    return (
      <SettingsRow
        title={title}
        description={isWithingsConnected ? connectedDescription : disconnectedDescription}
        action={actionButton}
        noStack
      />
    )
  }

  return actionButton
}
